import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import i18n from '../../translation/i18n';
import IssueGuide from '../IssueGuide';
import { IUser } from '../../atoms';
import { apiGet } from '../../services/_common';
import { getAgeFromCertainDate } from '../../utils/getAge';
import SkeletonTable from '../SkeletonTable';
import { ERROR } from '../../_constants';
import styled from 'styled-components';

const Root = styled.div<{ isSiteDashboard: boolean }>`
  height: 100%;
  position: relative;
  > .widget-header {
    width: 100%;
    padding: 0.75rem 1.5rem;
    > .widget-title {
      height: 2rem;
      > span {
        color: var(--widget-text-primary);
        font-weight: var(--font-semibold);
        font-size: var(--widget-font-size-title-sm);
      }
    }
    > .widget-subTitle {
      font-size: 0.875rem;
      line-height: 1rem;
      color: var(--text-secondary);
    }
  }
  > .widget-body {
    flex-grow: 1;
    width: 100%;
    padding: 0 1.5rem;
    margin-bottom: 1rem;

    user-select: none;
    display: grid;
    grid-template-columns: ${(props) => (props.isSiteDashboard ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)')};
    > div {
      gap: 0.75rem;
      > div:nth-child(1) {
        padding: ${(props) => (props.isSiteDashboard ? '0.625rem' : '1.2rem')};
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.5rem;
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
          font-size: 1.5rem;
          color: #fff;
        }
      }
      > div.red {
        background-color: ${(props) => (props.isSiteDashboard ? 'var(--widget-primary)' : 'var(--tonal-red)')};
      }
      > div.orange {
        background-color: ${(props) => (props.isSiteDashboard ? 'var(--widget-secondary)' : 'var(--tonal-orange)')};
      }
      > div.green {
        background-color: ${(props) => (props.isSiteDashboard ? 'var(--widget-theme-navy)' : 'var(--tonal-green)')};
      }
      > div.blue {
        background-color: ${(props) => (props.isSiteDashboard ? 'var(--widget-theme-darkblue)' : 'var(--tonal-blue)')};
      }
      > div:nth-child(2) {
        display: flex;
        flex-direction: ${(props) => (props.isSiteDashboard ? 'column' : 'row')};
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.25rem;
        color: var(--text-secondary);
        gap: 0.25rem;
        > div {
          font-size: ${(props) => (props.isSiteDashboard ? '1rem' : '1.125rem')};
          span {
            font-size: ${(props) => (props.isSiteDashboard ? '1rem' : '1.125rem')};
            font-weight: var(--font-semibold);
          }
        }
      }
    }
    > div.infoBox {
      gap: ${(props) => (props.isSiteDashboard ? '0.5rem' : '1rem')};
      font-size: 0.75rem;
      justify-content: ${(props) => (props.isSiteDashboard ? '' : 'space-between')};
      width: 100%;
      > .red > span {
        color: ${(props) => (props.isSiteDashboard ? '' : 'var(--red)')};
      }
      > .orange > span {
        color: ${(props) => (props.isSiteDashboard ? '' : 'var(--orange)')};
      }
      > .green > span {
        color: ${(props) => (props.isSiteDashboard ? '' : 'var(--green)')};
      }
      > .blue > span {
        color: ${(props) => (props.isSiteDashboard ? '' : 'var(--blue)')};
      }
      > div:nth-child(2) {
        gap: 0;
        width: ${(props) => (props.isSiteDashboard ? '' : '-webkit-fill-available')};
        span {
          margin-right: 0.125rem;
          font-size: var(--widget-font-size-lg);
        }
      }
    }
  }
`;

// 상수 정의
const WIDGET_TYPES = {
  TOTAL_INFO_BASIC: '003', // 신규/금일 출역/고령/일반
  TOTAL_INFO_REALTIME: '151', // 신규/금일 출역/퇴장/잔류
} as const;

// 종합 현황
export const TotalInfo = ({ name, userInfo, uniqueKey, refetchInterval }: { name: string; userInfo: IUser; uniqueKey: string; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  dayjs.locale(i18n.language === 'en-US' ? 'en' : 'ko');
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;
  const todayjs = dayjs();
  const [currentDate, setCurrentDate] = useState(todayjs);
  const format = currentDate.format('YYYYMMDD');

  const {
    data: newWorkerData,
    isLoading: isLoadingNewWorkerData,
    isError: isErrorNewWorkerData,
    refetch: refetchNewWorkerData,
  } = useQuery(['TotalInfoNewWorkerGet', userInfo], () => fetchNewWorkerData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
    onSettled: () => {
      setCurrentDate(dayjs());
    },
  });
  const {
    data: attendData,
    isLoading: isLoadingAttendData,
    isError: isErrorAttendData,
    refetch: refetchAttendData,
  } = useQuery(['attendCntGet', userInfo], () => fetchAttendData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
  });

  const {
    data: attendRealtimeData,
    isLoading: isLoadingAttendRealtimeData,
    isError: isErrorAttendRealtimeData,
    refetch: refetchAttendRealtimeData,
  } = useQuery(['attendRealtimeCntGet', userInfo], () => fetchAttendRealtimeData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
  });

  // 금일 신규 근로자 인원 수 조회
  const fetchNewWorkerData = async () => {
    try {
      const req = { wWorkstatus: 'I', wJobdate1: format, wJobdate2: format, hCd, sCd };
      const res = await apiGet({ path: '/worker', req });
      if (res.status === 200) {
        const newWorkerCnt = res.data.data.workerList.length;
        return { newWorkerCnt };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 금일 출역 요약 조회
  const fetchAttendData = async () => {
    try {
      const elderlyAge = userInfo.elderlyAgeSet || 65;
      const req = { aDate1: format, aDate2: format, hCd, sCd, elderlyAge: String(elderlyAge) };
      const res = await apiGet({ path: '/attend/day/summary', req });
      if (res.status === 200) {
        const { totalCnt = 0, oldWorkerCnt = 0, normalWorkerCnt = 0 } = res.data.data || {};
        return { todayAttendCnt: totalCnt, todayOldWorkerAttendCnt: oldWorkerCnt, todayNormalWorkerCnt: normalWorkerCnt };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 실시간 퇴장/잔류 인원 조회
  const fetchAttendRealtimeData = async () => {
    try {
      const [resIn, resOut] = await Promise.all([apiGet({ path: '/attend/realtime', req: { hCd, sCd, fInout: 'I' } }), apiGet({ path: '/attend/realtime', req: { hCd, sCd, fInout: 'O' } })]);
      if (resIn.status === 200 && resOut.status === 200) {
        const entryList = resIn.data.data.attendList;
        const exitList = resOut.data.data.attendList;
        return { attendInCnt: entryList.length, attendOutCnt: exitList.length };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickRefresh = () => {
    refetchNewWorkerData();
    refetchAttendData();
    refetchAttendRealtimeData();
  };

  const loading = isLoadingNewWorkerData || isLoadingAttendData || isLoadingAttendRealtimeData || !newWorkerData || !attendData;

  if (isErrorNewWorkerData || isErrorAttendData || isErrorAttendRealtimeData) return <IssueGuide text={ERROR} isImg={false} />;
  return (
    <Root isSiteDashboard={isSiteDashboard}>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t('종합 현황')}</span>
          {!isSiteDashboard && (
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          )}
        </div>
        <div className='widget-subTitle'>{currentDate?.format('MM월 DD일 dddd')}</div>
      </div>
      {loading ? (
        <SkeletonTable rows={1} />
      ) : (
        <div className='widget-body'>
          <div className='infoBox flex-basic'>
            <div className='red'>
              <span className='material-symbols-rounded'>person_add</span>
            </div>
            <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
              {!isSiteDashboard && <div className='infoBox-label'>{t('신규 근로자')}</div>}
              <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                <span className='count'>{newWorkerData?.newWorkerCnt ?? 0}</span>
                {t('명')}
              </div>
              {isSiteDashboard && t('신규 근로자')}
            </div>
          </div>
          <div className='infoBox flex-basic'>
            <div className='blue'>
              <span className='material-symbols-rounded'>leaderboard</span>
            </div>
            <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
              {!isSiteDashboard && <div className='infoBox-label'>{t('금일 출역')}</div>}
              <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                <span className='count'>{attendData?.todayAttendCnt ?? 0}</span>
                {t('명')}
              </div>
              {isSiteDashboard && t('금일 출역')}
            </div>
          </div>
          {WIDGET_TYPES.TOTAL_INFO_BASIC === uniqueKey ? (
            <>
              <div className='infoBox flex-basic'>
                <div className='orange'>
                  <span className='material-symbols-rounded'>groups</span>
                </div>
                <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                  {!isSiteDashboard && <div className='infoBox-label'>{t('금일 고령 출역')}</div>}
                  <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                    <span className='count'>{attendData?.todayOldWorkerAttendCnt ?? 0}</span>
                    {t('명')}
                  </div>
                  {isSiteDashboard && t('금일 고령 출역')}
                </div>
              </div>
              <div className='infoBox flex-basic'>
                <div className='green'>
                  <span className='material-symbols-rounded'>group</span>
                </div>
                <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                  {!isSiteDashboard && <div className='infoBox-label'>{t('금일 일반 출역')}</div>}
                  <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                    <span className='count'>{attendData?.todayNormalWorkerCnt ?? 0}</span>
                    {t('명')}
                  </div>
                  {isSiteDashboard && t('금일 일반 출역')}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='infoBox flex-basic'>
                <div className='orange'>
                  <span className='material-symbols-rounded'>groups</span>
                </div>
                <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                  {!isSiteDashboard && <div className='infoBox-label'>{t('퇴장 인원')}</div>}
                  <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                    <span className='count'>{attendRealtimeData?.attendOutCnt ?? 0}</span>
                    {t('명')}
                  </div>
                  {isSiteDashboard && t('퇴장 인원')}
                </div>
              </div>
              <div className='infoBox flex-basic'>
                <div className='green'>
                  <span className='material-symbols-rounded'>group</span>
                </div>
                <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                  {!isSiteDashboard && <div className='infoBox-label'>{t('실시간 잔류')}</div>}
                  <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                    <span className='count'>{attendRealtimeData?.attendInCnt ?? 0}</span>
                    {t('명')}
                  </div>
                  {isSiteDashboard && t('실시간 잔류')}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Root>
  );
};
